
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import GET_ACTIVITIES from "@/apollo/projectActivities/GetActivities.gql";
import GET_DECLINED_VISIT_MATCHES from "@/apollo/declinedVisitMatches/GetDeclinedVisitMatches.gql";
import GET_BOOKMARKED_CONTRACTORS_FOR_MANAGER from "@/apollo/smartMatch/GetBookmarkedContractorsForManager.gql";
import GET_BOOKMARKED_CONTRACTORS_FOR_ADMIN from "@/apollo/smartMatch/GetBookmarkedContractorsForAdmin.gql";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import PaginationConfig from "@/entities/PaginationConfig";
import Roles from "@/entities/Roles";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import { Project, Project_Activity, User } from "@/generated/graphql";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import DefaultContentLayout from "@/components/scaffold/DefaultContentLayoutNew.vue";
import VisitsCardWrapper from "@/components/projects/VisitsCardWrapper.vue";
import paginationConfig from "@/data/paginationConfig";

interface DeclinedMatch {
  id: string;
  user_id: string;
  match_percentage: number;
  user: {
    id: string;
    given_name: string;
    family_name: string;
    avatar?: string;
    profile?: {
      job_title?: string;
      occupation?: string;
    };
  };
}

interface BookmarkedContractor {
  id: string;
  user_id: string;
  match_percentage: number;
  bookmarked_at: string;
  user: {
    id: string;
    given_name: string;
    family_name: string;
    avatar?: string;
    profile?: {
      job_title?: string;
      occupation?: string;
    };
  };
}

@Component({
  components: {
    VisitsCardWrapper,
    EmptyListImageProjects,
    DefaultContentLayout,
    ActivityFormModal,
    EmptyListPlaceholder,
    CardPagination,
    LoadingOverlay,
  },
  apollo: {
    activities: {
      query: GET_ACTIVITIES,
      variables() {
        return this.activitiesArgs;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.project_activity.length;
        return data.project_activity;
      },
    },
  },
})
export default class Activities extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: String, required: true }) readonly userRole!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  activities: Project_Activity[] | null = null;
  dialog: boolean = false;
  selectedActivity?: Project_Activity = undefined;
  suggestedAssignee?: any = undefined;
  allDeclinedMatches: { [key: string]: DeclinedMatch[] } = {};
  allBookmarkedContractors: { [key: string]: BookmarkedContractor[] } = {};

  paginationConfig: PaginationConfig = { ...paginationConfig };

  get hasData(): boolean {
    return !this.isLoading && Boolean(this.activities);
  }

  get empty(): boolean {
    if (!this.activities) {
      return false;
    }
    return this.activities.length === 0;
  }

  get emptyListPlaceholder(): string {
    return `You currently have no open visits`;
  }

  get projectCompleted(): boolean {
    return this.project.completed;
  }

  get isAdmin(): boolean {
    return this.userRole === Roles.ADMIN;
  }

  get isManager(): boolean {
    return this.userRole === Roles.MANAGER;
  }

  get activitiesArgs(): object {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;
    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: [
          { project_id: { _eq: this.project.id } },
          { deleted: { _eq: false } },
          { completed: { _eq: false } },
          {
            _or: [
              {
                drafted: { _eq: false },
                accepted: { _is_null: true },
                contractor_id: { _is_null: false },
                contractor: { id: { _is_null: false } },
              },
              {
                accepted: { _is_null: true },
                _or: [
                  { contractor_id: { _is_null: true } },
                  { contractor: { id: { _is_null: true } } },
                ],
              },
              {
                drafted: { _eq: true },
                accepted: { _is_null: true },
              },
              {
                drafted: { _eq: false },
                accepted: { _eq: false },
              },
            ],
          },
        ],
      },
    };
  }

  get isLoading(): boolean {
    return this.$apollo.queries.activities.loading;
  }

  @Watch("activities")
  onActivitiesChange(): void {
    console.log('Activities changed:', {
      activities: this.activities,
      count: this.activities?.length,
      total: this.paginationConfig.total,
      isLoading: this.isLoading,
      hasData: this.hasData,
      empty: this.empty
    });
    if (this.activities && this.activities.length > 0) {
      this.fetchDeclinedMatchesForActivities();
      this.fetchBookmarkedContractorsForActivities();
    }
  }

  refetchVisits(): void {
    this.$apollo.queries.activities.refetch();
    this.$emit("activityListChanged");
  }

  getDeclinedMatchesForActivity(activityId: string): DeclinedMatch[] {
    return this.allDeclinedMatches[activityId] || [];
  }

  getBookmarkedContractorsForActivity(activityId: string): BookmarkedContractor[] {
    return this.allBookmarkedContractors[activityId] || [];
  }

  async fetchDeclinedMatchesForActivities(): Promise<void> {
    if (!this.activities || this.activities.length === 0) return;

    for (const activity of this.activities) {
      if (activity.created_by !== this.currentUserId) {
        this.$set(this.allDeclinedMatches, activity.id, []);
        continue;
      }

      try {
        const result = await this.$apollo.query({
          query: GET_DECLINED_VISIT_MATCHES,
          variables: {
            project_activity_id: activity.id
          }
        });

        const matches = result.data.declined_visit_matches || [];
        this.$set(this.allDeclinedMatches, activity.id, matches);
      } catch (error) {
        console.error(`Error fetching declined matches for activity ${activity.id}:`, error);
        this.$set(this.allDeclinedMatches, activity.id, []);
      }
    }
  }

  async fetchBookmarkedContractorsForActivities(): Promise<void> {
    if (!this.activities || this.activities.length === 0) return;

    for (const activity of this.activities) {
      if (activity.created_by !== this.currentUserId) {
        this.$set(this.allBookmarkedContractors, activity.id, []);
        continue;
      }

      const variables = {
        project_id: activity.project_id,
        manager_id: this.isManager && this.currentUser?.manager?.id ? this.currentUser.manager.id : null,
        admin_id: this.isAdmin && this.currentUser?.admin?.id ? this.currentUser.admin.id : null,
        exclude_user_id: activity.contractor?.user_id || null
      };

      try {
        if (!variables.manager_id && !variables.admin_id) {
          this.$set(this.allBookmarkedContractors, activity.id, []);
          continue;
        }

        let result;
        if (this.isManager && variables.manager_id) {
          result = await this.$apollo.query({
            query: GET_BOOKMARKED_CONTRACTORS_FOR_MANAGER,
            variables: {
              manager_id: variables.manager_id,
              project_id: variables.project_id
            }
          });
        } else if (this.isAdmin && variables.admin_id) {
          result = await this.$apollo.query({
            query: GET_BOOKMARKED_CONTRACTORS_FOR_ADMIN,
            variables: {
              admin_id: variables.admin_id,
              project_id: variables.project_id
            }
          });
        } else {
          this.$set(this.allBookmarkedContractors, activity.id, []);
          continue;
        }

        const bookmarks = result.data.smart_match_bookmark || [];
        this.$set(this.allBookmarkedContractors, activity.id, bookmarks);
      } catch (error) {
        console.error(`Error fetching bookmarked contractors for activity ${activity.id}:`, error);
        this.$set(this.allBookmarkedContractors, activity.id, []);
      }
    }
  }

  handleOpenActivityModal(eventData: { projectActivity: Project_Activity; suggestedAssignee: any }): void {
    this.selectedActivity = eventData.projectActivity;
    this.suggestedAssignee = eventData.suggestedAssignee;
    this.dialog = true;
  }

  closeActivityModal(): void {
    this.dialog = false;
    this.selectedActivity = undefined;
    this.suggestedAssignee = undefined;
  }
}
